import { useContext } from 'react';
import { Typography, Grid } from '@mui/material';
import { Check } from '@mui/icons-material';
import useKey from '@rooks/use-key';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormText,
    SttFormPassword,
    SttButton,
} from '../../../../sporttia/all';
import { useCrud } from '../../../../lib/hooks';

const Modes = {
    widget: 'widget',
    dialog: 'dialog',
};

type User = {
    id?: number;
    name?: string;
    role?: string;
    status?: string;
};

type LoginResponse = {
    user?: User;
    signature?: string;
};

type Props = {
    mode?: 'widget' | 'dialog';
    onLogin?: () => void;
};

/**
 * WidgetLogin: Present a self-contained widget login. NOTE: this component will call Context.setLoggedUser, which will force a refresh of the entire app
 * Props:
 *   string mode: either "widget" (fits anywhere) or "dialog" (column)
 *   function onLogin: callback to run after a successful login
 */
export default function WidgetLogin({
    mode = 'widget',
    onLogin,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [params, , setProperty, , Post] = useCrud<{
        login: string;
        password: string;
    }>(
        {
            login: '',
            password: '',
        },
        false,
    );

    useKey(['Enter'], doLogin);

    function doLogin() {
        Post(`/login`, params)
            .then((response) => {
                const { user } = response as LoginResponse;
                if (user) {
                    if (
                        user.role &&
                        (user.role !== cxt?.constants?.roleUser ||
                            user.role !== cxt?.constants?.roleTeacher)
                    ) {
                        cxt?.showMessage('E', t('login.onlyNormalUserAllowed'));
                    } else if (user.status === 'ACTIVE') {
                        cxt?.setLoggedUser(user);
                        onLogin?.();
                    } else {
                        cxt?.showMessage('E', t('login.userNotConfirmed'));
                    }
                }
            })
            .catch(({ error }: { error: { msg: string } }) => {
                cxt?.showMessage('E', error.msg);
            });
    }

    return (
        <div>
            <div>
                <Typography variant="body1">
                    {t('booking.mustLogin')}
                </Typography>
            </div>
            <div>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: mode === Modes.dialog ? 12 : 4 }}>
                        <SttFormText
                            autoFocus
                            name="login"
                            caption={t('login.loginOrEmail')}
                            defVal={params.login}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: mode === Modes.dialog ? 12 : 4 }}>
                        <SttFormPassword
                            name="password"
                            caption={t('user.password')}
                            defVal={params.password}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: mode === Modes.dialog ? 12 : 4 }}>
                        <SttButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            caption={t('login.enter')}
                            startIcon={<Check />}
                            onClick={doLogin}
                        />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}
